import { observer } from 'mobx-react-lite';
import { Badge, Button, ButtonProps, Divider, Group, Space, Stack, Title } from '@mantine/core';
import Icon from '@ui/components/Icon/Icon';
import Text from '@ui/components/Text/Text';

import BulletedList from '../../../../../../components/BulletedList/BulletedList';
import PlanCardPresenter from '../../../../../../presenters/PlanSettings/PlanCardPresenter';
import ConditionalWrapper from '../../../../../../components/ConditionalWrapper/ConditionalWrapper';
import Tooltip from '../../../../../../components/UI/Tooltip/Tooltip';
import PricingPlanPrice from '../../../../../../entities/pricing/pricingPlanPrice';

import './PlanCard.scss';

type PlanCardProps = {
	presenter: PlanCardPresenter,
	onUpgradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void,
	onDowngradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void,
	onCancelDowngradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void,
	onNoWorkspaceButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void,
	selectPlanMode?: boolean
};

const PRICE_STYLES = {
	fontSize: '48px',
	fontWeight: '700',
	lineHeight: '32px',
	letterSpacing: '0.45px'
};

const IN_TRIAL_BORDER_STYLES = {
	border: '2px solid var(--color-primary-base)'
};

const PlanCard = ( {
	presenter,
	onUpgradeButtonClicked,
	onDowngradeButtonClicked,
	onCancelDowngradeButtonClicked,
	onNoWorkspaceButtonClicked,
	selectPlanMode = false
}: PlanCardProps ) => {
	const renderChangeStatusButton = (
		text: string, type: ButtonProps['variant'],
		upgradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void,
		disabled = false
	) => (
		<ConditionalWrapper
			condition={disabled}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			wrapper={Tooltip as any}
			wrapperProps={{
				title: 'You cannot choose this plan because you might have<br/>add-ons or storage that reach the plan limit. You can<br/>try deleting seats or free storage space.'
			}}
		>
			<Button
				variant={type}
				size="large"
				fullWidth
				onClick={() => presenter.onUpgradeButtonClicked( upgradeButtonClicked )}
				disabled={disabled}
			>
				{text}
			</Button>
		</ConditionalWrapper>
	);

	const renderNoWorkspaceButton = (
		text: string, noWorkspaceButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void
	) => (
		renderChangeStatusButton( text, 'filled', noWorkspaceButtonClicked )
	);

	const renderUpgradeButton = (
		text: string, upgradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void
	) => (
		renderChangeStatusButton( text, 'filled', upgradeButtonClicked )
	);

	const renderDowngradeButton = (
		text: string, downgradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void
	) => (
		renderChangeStatusButton( text, 'link', downgradeButtonClicked )
	);

	const renderCancelDowngradeButton = (
		text: string, cancelDowngradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void
	) => (
		renderChangeStatusButton( text, 'filled', cancelDowngradeButtonClicked )
	);

	const renderDowngradePendingStatus = ( text: string ) => (
		<div className="downgradePendingBanner">
			<Icon icon="error" size={20} color="orange.6" />
			<div className="downgradePendingBannerText">
				<p>{text}</p>
			</div>
		</div>
	);

	const renderStayText = ( text: string ) => (
		<Text variant="body-bold" w="100%" style={{ textAlign: 'center', paddingBottom: '9px' }}>
			{text}
		</Text>
	);

	const renderReactivateStatus = (
		{ text, disabled }: { text: string, disabled: boolean },
		upgradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void
	) => (
		renderChangeStatusButton( text, 'filled', upgradeButtonClicked, disabled )
	);

	const statusToShow = ( currentStatus: { text: string, status: string, disabled: boolean } ) => {
		const selectableStatuses = [ 'upgrade', 'downgrade' ];
		const selectPlanText = selectPlanMode && selectableStatuses.includes( currentStatus.status )
			? 'Select plan'
			: currentStatus.text;
		const statusHash = {
			stay: renderStayText( currentStatus.text ),
			noWorkspace: renderNoWorkspaceButton( currentStatus.text, onNoWorkspaceButtonClicked ),
			upgrade: renderUpgradeButton( selectPlanText, onUpgradeButtonClicked ),
			downgrade: selectPlanMode
				? renderUpgradeButton( selectPlanText, onUpgradeButtonClicked )
				: renderDowngradeButton( currentStatus.text, onDowngradeButtonClicked ),
			reactivate: renderReactivateStatus( currentStatus, onUpgradeButtonClicked ),
			downgradePending: renderDowngradePendingStatus( currentStatus.text ),
			cancelDowngrade: renderCancelDowngradeButton(
				currentStatus.text,
				onCancelDowngradeButtonClicked
			)
		};

		return statusHash[ currentStatus.status as keyof typeof statusHash ];
	};

	return (
		<Stack className="PlanCard" justify="space-between" spacing="20px" style={presenter.isTrialingPlan ? IN_TRIAL_BORDER_STYLES : {}}>
			<Stack spacing="20px">
				<Group>
					<Title order={2}>{presenter.title}</Title>
					{presenter.isTrialingPlan && (
						<Badge color="blue" variant="light" style={{ border: 0, height: '18px' }}>
							<Text variant="footnote-semibold"> Currently On Trial</Text>
						</Badge>
					)}
				</Group>
				<Text variant="body" color="dark.3">{presenter.text}</Text>
				<Group className="price" spacing="10px">
					<Text style={PRICE_STYLES}>${presenter.cost}</Text>
					<Text variant="body-bold" w="110px">{presenter.costDescription}</Text>
				</Group>
				<Text variant="body-bold">{presenter.minimumMembersInfo}</Text>
				<Stack spacing={0}>
					<Divider color="blue.5" />
					{presenter.featuresSubstitle ? (
						<Text variant="body-bold" h="22px" mt="14px" mb="2px">{presenter.featuresSubstitle}</Text>
					) : (
						<Space h="28px" />
					)}
					<BulletedList color="blue.5" icon="check">
						{presenter.features.map( ( feature: string | { infoText: boolean, text: string } ) => (
							typeof feature === 'object' && feature.infoText
								? ( <p key={feature.text}>{feature.text}</p> )
								: <p key={feature as string}>{feature as string}</p>
						) )}
					</BulletedList>
				</Stack>
			</Stack>
			{ statusToShow( presenter.currentStatusToShow ) }
		</Stack>
	);
};

export default observer( PlanCard );
